import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { setActiveRole, type ChowRole } from '../../lib/actor'
import type { DockTab } from '../../lib/edition'
import { allowSeedContent } from '../../lib/runtime'

/**
 * ChooseChow actor context — the customer↔chef axis within the ChooseChow
 * edition. ChooseChow ships one app whose `authClasses` are
 * `['customer', 'chef', 'market_vendor']` (see `lib/edition.ts`); a chef is also
 * a customer, but the active *role* changes what each page leads with (a
 * customer browses & orders; a chef manages their kitchen & sees prep-side
 * copy).
 *
 * Identity is authoritative from the authenticated session: a user signs in as
 * a role, and `role`/`profileId` come from `chow_session` (see AuthContext,
 * which calls `setRole`/`setProfileId` on login). On a live build that is the
 * only source. In dev, a `?role=` (or `?as=`) URL override plus per-role
 * placeholder profile ids let one browser flip between the customer and chef
 * surfaces without re-authenticating — a testing affordance, gated off on live
 * so no fabricated identity can reach the backend.
 */

/** Dev-only: allow the `?role=` URL override + placeholder profile ids. */
const allowRoleOverride = allowSeedContent()

export type { ChowRole }

const ROLE_QUERY_KEYS = ['role', 'as']
const ROLE_STORAGE_KEY = 'chow_role'

const normalizeRole = (value: string | null | undefined): ChowRole | null => {
  switch ((value ?? '').toLowerCase()) {
    case 'customer':
    case 'eater':
    case 'buyer':
      return 'customer'
    case 'chef':
    case 'vendor':
    case 'market_vendor':
    case 'cook':
    case 'kitchen':
      return 'chef'
    default:
      return null
  }
}

const roleFromSearch = (search: string): ChowRole | null => {
  const params = new URLSearchParams(search)
  for (const key of ROLE_QUERY_KEYS) {
    const found = normalizeRole(params.get(key))
    if (found) return found
  }
  return null
}

const readStoredRole = (): ChowRole | null => {
  try {
    return normalizeRole(sessionStorage.getItem(ROLE_STORAGE_KEY))
  } catch {
    return null
  }
}

/** Dev-only per-role placeholder profiles, matching the demo DB seed. */
const PROFILE_ID: Record<ChowRole, string> = {
  customer: '11111111-0000-4000-8000-000000000001',
  chef: '11111111-0000-4000-8000-000000000002',
}

interface StoredSession {
  id?: string
  profileId?: string
  profile_id?: string
  role?: string
}

const readSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem('chow_session')
    return raw ? (JSON.parse(raw) as StoredSession) : null
  } catch {
    return null
  }
}

/** The authenticated role, if a session names one. */
const readSessionRole = (): ChowRole | null => {
  const session = readSession()
  return session ? normalizeRole(session.role) : null
}

/**
 * The profile id sent on writes. Authoritative from the session when its role
 * matches the active role. In dev, a role the session isn't signed in as falls
 * back to a placeholder so the other surface can be exercised; on live there is
 * no fabrication — the authenticated profile id is used, or empty (no identity
 * is better than a fabricated one).
 */
const readStoredProfileId = (role: ChowRole): string => {
  const session = readSession()
  const sessionProfileId = session?.profileId || session?.profile_id
  if (sessionProfileId && session && normalizeRole(session.role) === role) return String(sessionProfileId)
  if (allowRoleOverride) return PROFILE_ID[role]
  return sessionProfileId ? String(sessionProfileId) : ''
}

/**
 * Customer dock = browse the marketplace. Chef dock pivots the app to kitchen
 * *management*: the queue & dispatch, menu curation, earnings — chef actions,
 * not browsing. Home for both is `/dashboard` (the app lives under the auth gate;
 * `/` is the public landing). Glyph keys reuse the existing dock icon set.
 */
const customerTabs: readonly DockTab[] = [
  { path: '/dashboard', label: 'Home', icon: 'home' },
  { path: '/dishes', label: 'Explore', icon: 'dish' },
  { path: '/orders', label: 'Orders', icon: 'cart' },
  // Customers can plan meals without a paid membership.
  { path: '/mealplan', label: 'Meal plans', icon: 'active' },
  { path: '/profile', label: 'Profile', icon: 'profile' },
]
const chefTabs: readonly DockTab[] = [
  { path: '/dashboard', label: 'Kitchen', icon: 'home' },
  { path: '/orders', label: 'Dispatch', icon: 'active' },
  { path: '/menu', label: 'Menus', icon: 'dish' },
  { path: '/wallet', label: 'Earnings', icon: 'earnings' },
  { path: '/profile', label: 'Profile', icon: 'profile' },
]

export interface ChooseChowContextValue {
  role: ChowRole
  /** The active actor's profile id, sent on writes (orders, subscriptions…). */
  profileId: string
  isChef: boolean
  /** Role-appropriate bottom-dock tabs (customer browse vs chef management). */
  tabs: readonly DockTab[]
  setRole: (role: ChowRole) => void
  setProfileId: (id: string) => void
}

const ChooseChowContext = createContext<ChooseChowContextValue | null>(null)

export const ChooseChowProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const [role, setRoleState] = useState<ChowRole>(
    () =>
      (allowRoleOverride
        ? roleFromSearch(typeof window !== 'undefined' ? window.location.search : '')
        : null) ??
      readSessionRole() ??
      readStoredRole() ??
      'customer',
  )
  const [profileId, setProfileIdState] = useState<string>(() => readStoredProfileId(role))

  // Keep the module-level actor cache in sync so commands carry X-Chow-Role.
  useEffect(() => {
    setActiveRole(role)
  }, [role])

  useEffect(() => {
    // The `?role=` override is a dev testing affordance; on a live build the
    // role is fixed to the authenticated session.
    if (!allowRoleOverride) return
    const fromUrl = roleFromSearch(location.search)
    if (fromUrl && fromUrl !== role) {
      setRoleState(fromUrl)
      setProfileIdState(readStoredProfileId(fromUrl))
      try {
        sessionStorage.setItem(ROLE_STORAGE_KEY, fromUrl)
      } catch {
        /* sessionStorage unavailable — state is enough */
      }
    }
  }, [location.search, role])

  const value = useMemo<ChooseChowContextValue>(
    () => ({
      role,
      profileId,
      isChef: role === 'chef',
      tabs: role === 'chef' ? chefTabs : customerTabs,
      setRole: (next) => {
        setRoleState(next)
        setProfileIdState(readStoredProfileId(next))
        try {
          sessionStorage.setItem(ROLE_STORAGE_KEY, next)
        } catch {
          /* ignore */
        }
      },
      setProfileId: (id) => {
        setProfileIdState(id)
      },
    }),
    [role, profileId],
  )

  return <ChooseChowContext.Provider value={value}>{children}</ChooseChowContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useChooseChow = (): ChooseChowContextValue => {
  const ctx = useContext(ChooseChowContext)
  if (!ctx) {
    throw new Error('useChooseChow must be used within a ChooseChowProvider')
  }
  return ctx
}

/**
 * One back-navigation rule for the whole app, so every header speaks the same
 * language. A screen that IS a top-level dock destination for the active role
 * (Home/Chefs/…/Profile for a customer; Kitchen/Dispatch/…/Profile for a chef)
 * is reached by tapping the dock, so it shows no back button (returns
 * `undefined`). Every other screen — profile sub-pages, detail pages, cart — is
 * a sub-page and gets a back affordance that returns to the previous screen,
 * falling back to Profile when there is no in-app history (a deep link).
 *
 * `/orders` and `/wallet` are dock tabs for a chef but profile sub-pages for a
 * customer, so this rule is role-aware and resolves each correctly.
 */
export const useBackNav = (): (() => void) | undefined => {
  const navigate = useNavigate()
  const location = useLocation()
  const { tabs } = useChooseChow()
  const isTopLevel = tabs.some((tab) => tab.path === location.pathname)
  if (isTopLevel) {
    return undefined
  }
  return () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1)
    } else if (location.pathname.startsWith('/kitchen')) {
      navigate('/kitchens')
    } else if (location.pathname.startsWith('/dish')) {
      navigate('/dishes')
    } else {
      navigate('/dashboard')
    }
  }
}

/* ── page-relative context copy ──────────────────────────────────────── */

export interface PageContext {
  title: string
  subtitle: string
}

type PageCopy = Record<ChowRole, PageContext>

/**
 * Per-route header copy keyed by role. Pages read this so the same route reads
 * as a customer surface ("find a chef") or a chef surface ("your kitchen")
 * depending on the active actor. Unknown routes fall back to a neutral entry.
 */
const PAGE_COPY: Record<string, PageCopy> = {
  '/': {
    customer: { title: 'ChooseChow', subtitle: '' },
    chef: { title: 'Your kitchen', subtitle: '' },
  },
  '/kitchens': {
    customer: { title: 'Kitchens', subtitle: '' },
    chef: { title: 'Nearby kitchens', subtitle: '' },
  },
  '/dishes': {
    customer: { title: 'Dishes', subtitle: '' },
    chef: { title: 'Your menu', subtitle: '' },
  },
  '/menus': {
    customer: { title: 'Kitchen menus', subtitle: '' },
    chef: { title: 'Your menus', subtitle: '' },
  },
  '/market': {
    customer: { title: 'Market', subtitle: '' },
    chef: { title: 'Market', subtitle: '' },
  },
  '/profile': {
    customer: { title: 'Profile', subtitle: '' },
    chef: { title: 'Chef profile', subtitle: '' },
  },
  '/subscriptions': {
    customer: { title: 'My meal plan', subtitle: '' },
    chef: { title: 'Your meal plans', subtitle: '' },
  },
  '/mealplan': {
    customer: { title: 'My meal plan', subtitle: '' },
    chef: { title: 'My meal plan', subtitle: '' },
  },
  '/memberships': {
    customer: { title: 'My meal plan', subtitle: '' },
    chef: { title: 'My meal plan', subtitle: '' },
  },
  '/orders': {
    customer: { title: 'Orders', subtitle: '' },
    chef: { title: 'Dispatch', subtitle: '' },
  },
  '/verification': {
    customer: { title: 'Verification', subtitle: '' },
    chef: { title: 'Verification', subtitle: '' },
  },
  '/schedule': {
    customer: { title: 'Working hours', subtitle: '' },
    chef: { title: 'Working hours', subtitle: '' },
  },
  '/menu': {
    customer: { title: 'Dishes', subtitle: '' },
    chef: { title: 'Your menus', subtitle: '' },
  },
  '/menu/new': {
    customer: { title: 'New menu', subtitle: '' },
    chef: { title: 'New menu', subtitle: '' },
  },
  '/wallet': {
    customer: { title: 'Wallet', subtitle: '' },
    chef: { title: 'Earnings', subtitle: '' },
  },
}

const NEUTRAL: PageContext = { title: 'ChooseChow', subtitle: '' }

/** Resolve the role-aware header copy for the current route. */
// eslint-disable-next-line react-refresh/only-export-components
export const usePageContext = (pathname: string): PageContext => {
  const { role } = useChooseChow()
  const copy = PAGE_COPY[pathname]
  return copy ? copy[role] : NEUTRAL
}
